import * as Actions from '../actions/invoiceBatchItemActions';
import InvoiceBatchItem from '../../entities/invoiceBatchItem';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type InvoiceBatchItemAction = { 
    type: string; 
    invoiceBatchItem: InvoiceBatchItem | null; 
    invoiceBatchItemId: string | InvoiceBatchItem; 
    partial: boolean; 
    lastFetch: number; 
    invoiceBatchItems: InvoiceBatchItem[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<InvoiceBatchItem> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {InvoiceBatchItem[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<InvoiceBatchItem> | null, firmId: string, userId: string, updates?: InvoiceBatchItem[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'invoiceBatchItem', 'invoiceBatchItems', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<InvoiceBatchItem>} data
 * @param {InvoiceBatchItem[]} [updates]
 * @returns {ArrayMap<InvoiceBatchItem>}
 */
function updateStorage(data: ArrayMap<InvoiceBatchItem>, updates?: InvoiceBatchItem[]): ArrayMap<InvoiceBatchItem> {
	if (logging) console.debug('⮂ invoiceBatchItemReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {InvoiceBatchItem[]} [updates]
 * @returns {ArrayMap<InvoiceBatchItem>}
 */
function updateStorageNoReturn(updates?: InvoiceBatchItem[]): void {
	if (logging) console.debug('⮂ invoiceBatchItemReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {InvoiceBatchItem[]} [data]
 * @returns {ArrayMap<InvoiceBatchItem>}
 */
function createState(data?: InvoiceBatchItem[] | ArrayMap<InvoiceBatchItem>): ArrayMap<InvoiceBatchItem> {
	if (!data) return ArrayMapRegistry.clone<InvoiceBatchItem>('invoiceBatchItem', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<InvoiceBatchItem>('invoiceBatchItem', data.map(e => e));
    else return ArrayMapRegistry.clone<InvoiceBatchItem>('invoiceBatchItem', data);
}

function invoiceBatchItems(state: ArrayMap<InvoiceBatchItem>, action: InvoiceBatchItemAction): ArrayMap<InvoiceBatchItem> {
	if (!state) state = createState();

	let st: ArrayMap<InvoiceBatchItem>;

	switch (action.type) {
		// action.invoiceBatchItem - the new value of invoiceBatchItem with id action.id
		// action.invoiceBatchItemId - id of the target, used if new value is null
		case Actions.INVOICEBATCHITEM_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ invoiceBatchItemReducer: FETCH_ONE_SUCCESS', action);

			if (action.invoiceBatchItem === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.invoiceBatchItemId)) {
				st = state.clone();
				st.set(action.invoiceBatchItem);
				// state.clear();

				updateStorageNoReturn([action.invoiceBatchItem]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.invoiceBatchItems
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.invoiceBatchItems
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.invoiceBatchItems
			 !partial, !stateEmpty, fetchEmpty	-> D return invoiceBatchItems in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge invoiceBatchItems in state newer than those in action.invoiceBatchItems
		 */
		case Actions.INVOICEBATCHITEM_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.invoiceBatchItems.length === 0;
			const fetched = action.invoiceBatchItems.map(invoiceBatchItem => new InvoiceBatchItem({...invoiceBatchItem, persisted: true}));

			if (logging) console.debug(`⮂ invoiceBatchItemReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} invoiceBatchItems with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ invoiceBatchItemReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ invoiceBatchItemReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<InvoiceBatchItem> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: InvoiceBatchItem[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(invoiceBatchItem => {
					const current = st.get(invoiceBatchItem.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && invoiceBatchItem.status === 'D') {
							// Remove it from state
							toUpdate.push(invoiceBatchItem);
							st.delete(invoiceBatchItem);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (invoiceBatchItem.addedOn > current.addedOn) {
								toUpdate.push(invoiceBatchItem);
								st.set(invoiceBatchItem);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (invoiceBatchItem.status !== 'D') {
							toUpdate.push(invoiceBatchItem);
							st.set(invoiceBatchItem);
						}
						else if (logging) console.debug('⮂ invoiceBatchItemReducer: partialMerge: skipping deleted invoiceBatchItem not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ invoiceBatchItemReducer: empty: invoiceBatchItems', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ invoiceBatchItemReducer: fullMerge: invoiceBatchItems', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localInvoiceBatchItem => {
                    // If this invoiceBatchItem has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localInvoiceBatchItem.persisted) {
                        const fetchedInvoiceBatchItem = st.get(localInvoiceBatchItem.id);
                        if (!fetchedInvoiceBatchItem || localInvoiceBatchItem.addedOn > fetchedInvoiceBatchItem.addedOn) st.set(localInvoiceBatchItem);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.INVOICEBATCHITEM_FETCH_ONE_FAILURE:
			console.error('INVOICEBATCHITEM_FETCH_ONE_FAILURE', action.invoiceBatchItemId, action.err);

			// No update to storage required
			return state;

		case Actions.INVOICEBATCHITEM_FETCH_FAILURE:
			console.error('INVOICEBATCHITEM_FETCH_FAILURE', action.invoiceBatchItemId, action.err);

			// No update to storage required
			return state;

		case Actions.INVOICEBATCHITEM_RESTORE: {
		    let invoiceBatchItem = new InvoiceBatchItem({...action.invoiceBatchItem});
            st = state.clone();

            const existing = st.get(invoiceBatchItem.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (invoiceBatchItem.addedOn >= existing.addedOn) {
                    st.set(invoiceBatchItem);
                    return updateStorage(st, [invoiceBatchItem]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (invoiceBatchItem.status === 'D') return state;

                st.set(invoiceBatchItem);
                return updateStorage(st, [invoiceBatchItem]);
            }
        }
            
		case Actions.INVOICEBATCHITEM_SAVE: {
		    // console.debug('⮂ INVOICEBATCHITEM_SAVE reducer', action.invoiceBatchItem?.id);
		    
			// clone the invoiceBatchItem
			let invoiceBatchItem = new InvoiceBatchItem({...action.invoiceBatchItem});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new invoiceBatchItem in the state
			// If this is a create, add to the queue
			const _invoiceBatchItem = st.get(invoiceBatchItem.id);

			if (!_invoiceBatchItem) st.set(invoiceBatchItem);
			else {
				if (invoiceBatchItem.addedOn >= _invoiceBatchItem.addedOn) st.set(invoiceBatchItem);
			}

			return updateStorage(st, [invoiceBatchItem]);
		}

		case Actions.INVOICEBATCHITEM_SAVE_MANY: {
		    if (logging) console.debug('⮂ INVOICEBATCHITEM_SAVE_MANY reducer', action.invoiceBatchItems.map(invoiceBatchItem => invoiceBatchItem.id).join(','));
		    
			// clone the invoiceBatchItems
			let toProcess = action.invoiceBatchItems.map(invoiceBatchItem => new InvoiceBatchItem(invoiceBatchItem));

			st = state.clone();
			// state.clear();

			const updates: InvoiceBatchItem[] = [];

			toProcess.forEach(invoiceBatchItem => {
				// Add or update the new invoiceBatchItem in the state
				// If this is a create, add to the queue
				const existing = st.get(invoiceBatchItem.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (invoiceBatchItem.status !== 'D') {
						st.push(invoiceBatchItem);
						updates.push(invoiceBatchItem);
					}
					else if (logging) console.debug('⮂ INVOICEBATCHITEM_SAVE_MANY: skipping deleted invoiceBatchItem not in state');
				}
				else {
					if (invoiceBatchItem.addedOn >= existing.addedOn) {
						st.set(invoiceBatchItem);
						updates.push(invoiceBatchItem);
					}
					else if (logging) console.debug('⮂ Skipping, existing invoiceBatchItem had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.INVOICEBATCHITEM_REMOVE: {
			// clone the invoiceBatchItem
			const invoiceBatchItem = new InvoiceBatchItem({...action.invoiceBatchItem});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(invoiceBatchItem);

			return updateStorage(st, [invoiceBatchItem]);
		}

		case Actions.INVOICEBATCHITEM_RESTORE_FAILURE:
		case Actions.INVOICEBATCHITEM_SAVE_FAILURE: {
		    console.warn('INVOICEBATCHITEM_SAVE_FAILURE reducer', action.invoiceBatchItem?.id);
		    if (!action.invoiceBatchItem?.id) {
				console.warn('INVOICEBATCHITEM_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const invoiceBatchItem = ensure(InvoiceBatchItem, action.invoiceBatchItem);
			if (!invoiceBatchItem) {
				console.error('Could not find invoiceBatchItem after call to save');
				return state;
			}

			st = state.clone();

			const _invoiceBatchItemRef = state.get(invoiceBatchItem.id);

			if (_invoiceBatchItemRef && (_invoiceBatchItemRef.addedOn === invoiceBatchItem.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _invoiceBatchItem = new InvoiceBatchItem({..._invoiceBatchItemRef});
				_invoiceBatchItem.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_invoiceBatchItem.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('invoiceBatchItem', _invoiceBatchItem).then(r => {
						if (r.isErr()) console.error(`⮂ invoiceBatchItemReducer: failed to save invoiceBatchItem with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ invoiceBatchItemReducer: saved invoiceBatchItem with fatalError info');
					}).catch(err => console.error(`⮂ invoiceBatchItemReducer: failed to save invoiceBatchItem with fatalError info: ${err.message}`));

				st.set(_invoiceBatchItem);
			}

			return updateStorage(st);
		}

		case Actions.INVOICEBATCHITEM_SAVE_MANY_FAILURE: {
		    console.warn('INVOICEBATCHITEM_SAVE_MANY_FAILURE reducer', action.invoiceBatchItems.map(invoiceBatchItem => invoiceBatchItem.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.INVOICEBATCHITEM_RESTORE_SUCCESS:
		case Actions.INVOICEBATCHITEM_SAVE_SUCCESS: {
			if (!action.invoiceBatchItem?.id) {
				console.warn('INVOICEBATCHITEM_SAVE_SUCCESS reducer');
                return state;
			}

			const invoiceBatchItemRef = state.get(action.invoiceBatchItem?.id);
			if (!invoiceBatchItemRef) {
				console.warn('Could not find invoiceBatchItem after call to save');
                return state;
			}

			st = state.clone();

			if (action.invoiceBatchItem.addedOn >= invoiceBatchItemRef.addedOn) {
				st.set(action.invoiceBatchItem);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const invoiceBatchItem = new InvoiceBatchItem({...invoiceBatchItemRef, persisted: true});
				st.set(invoiceBatchItem);
			}

			return updateStorage(st, [action.invoiceBatchItem]);
		}

		case Actions.INVOICEBATCHITEM_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ INVOICEBATCHITEM_SAVE_MANY_SUCCESS reducer', action.invoiceBatchItems.map(invoiceBatchItem => invoiceBatchItem.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const invoiceBatchItems: InvoiceBatchItem[] = [];

			action.invoiceBatchItems.forEach(e => {
				const invoiceBatchItemRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!invoiceBatchItemRef) console.warn('Could not find invoiceBatchItem after call to save');
				else if (e.addedOn >= invoiceBatchItemRef.addedOn) {
				    st.set(e);
				    invoiceBatchItems.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const invoiceBatchItem = new InvoiceBatchItem({...invoiceBatchItemRef, persisted: true});
					st.set(invoiceBatchItem);
					invoiceBatchItems.push(invoiceBatchItem);
				}
			});

			return updateStorage(st, invoiceBatchItems);
		}

		case Actions.INVOICEBATCHITEM_REMOVE_FAILURE: {
		    console.warn('INVOICEBATCHITEM_REMOVE_FAILURE action', action.invoiceBatchItem?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.INVOICEBATCHITEM_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.INVOICEBATCHITEM_RESTORE_MANY: {
			if (logging) console.debug('⮂ INVOICEBATCHITEM_RESTORE_MANY reducer', action.invoiceBatchItems.map(invoiceBatchItem => invoiceBatchItem.id).join(','));

			const toProcess = action.invoiceBatchItems.map(invoiceBatchItem => new InvoiceBatchItem({...invoiceBatchItem}));
			st = state.clone();

			const updates: InvoiceBatchItem[] = [];

			toProcess.forEach(invoiceBatchItem => {
				const existing = st.get(invoiceBatchItem.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (invoiceBatchItem.addedOn >= existing.addedOn) {
						st.set(invoiceBatchItem);
						updates.push(invoiceBatchItem);
					}
					else if (logging) console.debug('⮂ INVOICEBATCHITEM_RESTORE_MANY: skipping older version of invoiceBatchItem');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (invoiceBatchItem.status !== 'D') {
						st.set(invoiceBatchItem);
						updates.push(invoiceBatchItem);
					}
					else if (logging) console.debug('⮂ INVOICEBATCHITEM_RESTORE_MANY: skipping deleted invoiceBatchItem not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.INVOICEBATCHITEM_REMOVE_MANY: {
			if (logging) console.debug('⮂ INVOICEBATCHITEM_REMOVE_MANY reducer', action.invoiceBatchItems.map(invoiceBatchItem => invoiceBatchItem.id).join(','));

			const toProcess = action.invoiceBatchItems.map(invoiceBatchItem => new InvoiceBatchItem({...invoiceBatchItem}));
			st = state.clone();

			toProcess.forEach(invoiceBatchItem => {
				st.delete(invoiceBatchItem);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default invoiceBatchItems;